import React, { useState, useEffect } from 'react'
import { useAuth, useUserId } from '@talktoast/shared'
import {
  favoritesApiService,
  FavoriteRecipe,
  recipeApiService,
  RecipeRecord
} from '@talktoast/shared'
import { Loading, Button, Card, EmptyState } from '@talktoast/ui'
import { ICONS, getIconSize, MEAL_TYPE_EMOJIS } from '@talktoast/shared'
import RecipeCard from './RecipeCard'

interface Recipe {
  id: string
  name: string
  description: string
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks'
  prepTime?: number
  cookTime?: number
  servings?: number
  rating?: 'up' | 'down' | null
  dateUsed: string
  sessionId?: string
  // New fields for favorites
  thumbsRating?: -1 | 0 | 1
  isFavorite?: boolean
  recipeData?: any
  recipeHash?: string
}

type SingleTierFilter = 'all' | 'favorites' | 'liked' | 'disliked' | 'breakfast' | 'lunch' | 'dinner' | 'snacks'

interface RecipeHistoryScreenProps {
  onBack?: () => void
  initialFilter?: 'all' | 'liked' | 'disliked' | 'favorites'
}

const RecipeHistoryScreen: React.FC<RecipeHistoryScreenProps> = ({ onBack, initialFilter = 'all' }) => {
  const { isGuest } = useAuth()
  const userId = useUserId() // Custom UUID architecture - works for both guest and authenticated users
  const [recipes, setRecipes] = useState<Recipe[]>([])
  const [filter, setFilter] = useState<SingleTierFilter>(initialFilter)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // No notes cache needed - notes are part of recipe record

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  useEffect(() => {
    loadRecipeHistory()
  }, [userId])

  const loadRecipeHistory = async () => {
    setLoading(true)
    setError(null)
    try {
      if (!userId) {
        console.warn('[RecipeHistoryScreen] No user ID available')
        setRecipes([])
        return
      }

      // Load all recipes from recipes table
      let allRecipes: RecipeRecord[]
      let favorites: FavoriteRecipe[]

      if (isGuest) {
        // For guests, load from localStorage (both recipes and favorites)
        // TODO: Implement guest recipe storage in localStorage
        favorites = favoritesApiService.getGuestFavorites(userId)
        console.log(`[RecipeHistoryScreen] Loaded ${favorites.length} guest favorites`)

        // Convert favorites to recipes format for now (guest full history not yet implemented)
        allRecipes = favorites.map(fav => ({
          id: fav.id,
          user_id: userId,
          session_id: '',
          recipe_data: fav.recipe_data,
          assigned_dates: [],
          is_cooked: false,
          cooked_at: null,
          rating: null,
          notes: null,
          created_at: fav.created_at,
          updated_at: fav.updated_at
        }))
      } else {
        // For authenticated users, load from database
        allRecipes = await recipeApiService.getAllUserRecipes(userId)
        favorites = await favoritesApiService.getUserFavorites(userId)
        console.log(`[RecipeHistoryScreen] Loaded ${allRecipes.length} recipes and ${favorites.length} favorites from database`)
      }

      // Create a map of favorites by recipe name for quick lookup
      const favoritesMap = new Map<string, FavoriteRecipe>()
      favorites.forEach(fav => {
        favoritesMap.set(fav.recipe_title.toLowerCase(), fav)
      })

      // Merge recipes with favorites data
      const recipesData: Recipe[] = allRecipes.map(recipe => {
        const recipeName = recipe.recipe_data?.name || ''
        const favorite = favoritesMap.get(recipeName.toLowerCase())

        return {
          id: recipe.id,
          name: recipeName,
          description: recipe.recipe_data?.description || '',
          mealType: (recipe.recipe_data?.mealType || 'dinner') as 'breakfast' | 'lunch' | 'dinner' | 'snacks',
          prepTime: recipe.recipe_data?.prepTime,
          cookTime: recipe.recipe_data?.cookTime,
          servings: recipe.recipe_data?.servings,
          dateUsed: recipe.created_at,
          sessionId: recipe.session_id || undefined,
          // Overlay favorites data if it exists
          thumbsRating: favorite?.thumbs_rating ?? 0,
          isFavorite: favorite?.is_favorite ?? false,
          recipeData: recipe.recipe_data,
          recipeHash: favorite?.recipe_hash || favoritesApiService.generateRecipeHash(recipeName),
          // Legacy field for backwards compatibility
          rating: favorite?.thumbs_rating === 1 ? 'up' : favorite?.thumbs_rating === -1 ? 'down' : null
        }
      })

      setRecipes(recipesData)
    } catch (err) {
      console.error('[RecipeHistoryScreen] Failed to load recipe history:', err)
      setError('Failed to load recipe history')
    } finally {
      setLoading(false)
    }
  }

  const handleThumbsUp = async (recipe: Recipe) => {
    try {
      if (!userId) return

      const recipeHash = recipe.recipeHash || favoritesApiService.generateRecipeHash(recipe.name)

      // Smart toggle logic:
      // - If currently thumbs up (1), toggle to neutral (0)
      // - If currently thumbs down (-1) or neutral (0), set to thumbs up (1)
      const newRating = recipe.thumbsRating === 1 ? 0 : 1

      // Optimistic UI update - instant feedback
      const optimisticRecipes = recipes.map(r =>
        r.id === recipe.id
          ? {
              ...r,
              thumbsRating: newRating,
              rating: newRating === 1 ? 'up' as const : null
            }
          : r
      )
      setRecipes(optimisticRecipes)

      // Background API call - no loading state
      try {
        await favoritesApiService.updateRecipeRating(userId, {
          recipeHash,
          recipeName: recipe.name,
          sessionId: recipe.sessionId,
          thumbsRating: newRating as -1 | 0 | 1
        })
      } catch (error) {
        // Rollback on error
        setRecipes(recipes)
        console.error('[RecipeHistoryScreen] Failed to update thumbs up:', error)
        setError('Failed to update rating')
        setTimeout(() => setError(null), 3000)
      }
    } catch (error) {
      console.error('[RecipeHistoryScreen] Failed to update thumbs up:', error)
      setError('Failed to update rating')
      setTimeout(() => setError(null), 3000)
    }
  }

  const handleThumbsDown = async (recipe: Recipe) => {
    try {
      if (!userId) return

      const recipeHash = recipe.recipeHash || favoritesApiService.generateRecipeHash(recipe.name)

      // Smart toggle logic:
      // - If currently thumbs down (-1), toggle to neutral (0)
      // - If currently thumbs up (1) or neutral (0), set to thumbs down (-1)
      const newRating = recipe.thumbsRating === -1 ? 0 : -1

      // Backend automatically removes from favorites when thumbs down is set
      const newFavoriteState = newRating === -1 ? false : recipe.isFavorite

      // Optimistic UI update - instant feedback
      const optimisticRecipes = recipes.map(r =>
        r.id === recipe.id
          ? {
              ...r,
              thumbsRating: newRating,
              isFavorite: newFavoriteState,
              rating: newRating === -1 ? 'down' as const : null
            }
          : r
      )
      setRecipes(optimisticRecipes)

      // Background API call - no loading state
      try {
        await favoritesApiService.updateRecipeRating(userId, {
          recipeHash,
          recipeName: recipe.name,
          sessionId: recipe.sessionId,
          thumbsRating: newRating as -1 | 0 | 1
        })
      } catch (error) {
        // Rollback on error
        setRecipes(recipes)
        console.error('[RecipeHistoryScreen] Failed to update thumbs down:', error)
        setError('Failed to update rating')
        setTimeout(() => setError(null), 3000)
      }
    } catch (error) {
      console.error('[RecipeHistoryScreen] Failed to update thumbs down:', error)
      setError('Failed to update rating')
      setTimeout(() => setError(null), 3000)
    }
  }

  const handleToggleFavorite = async (recipe: Recipe) => {
    try {
      if (!userId) return

      // Cannot favorite if thumbs down
      if (recipe.thumbsRating === -1 && !recipe.isFavorite) {
        setError('Cannot favorite a recipe that has been thumbs-downed')
        setTimeout(() => setError(null), 3000)
        return
      }

      const newFavoriteState = !recipe.isFavorite

      // When favoriting (not un-favoriting), auto-like if not already liked
      const needsAutoLike = newFavoriteState && recipe.thumbsRating !== 1
      const newThumbsRating = needsAutoLike ? 1 : recipe.thumbsRating

      // Optimistic UI update - instant feedback
      const optimisticRecipes = recipes.map(r =>
        r.id === recipe.id
          ? {
              ...r,
              isFavorite: newFavoriteState,
              thumbsRating: newThumbsRating,
              rating: newThumbsRating === 1 ? 'up' as const : newThumbsRating === -1 ? 'down' as const : null
            }
          : r
      )
      setRecipes(optimisticRecipes)

      // Background API calls - no loading state
      try {
        // Toggle favorite
        await favoritesApiService.toggleFavorite(userId, recipe.name, recipe.sessionId)

        // Auto-like if needed
        if (needsAutoLike) {
          const recipeHash = recipe.recipeHash || favoritesApiService.generateRecipeHash(recipe.name)
          await favoritesApiService.updateRecipeRating(userId, {
            recipeHash,
            recipeName: recipe.name,
            sessionId: recipe.sessionId,
            thumbsRating: 1
          })
        }
      } catch (error: any) {
        // Rollback on error - restore previous state
        setRecipes(recipes)
        console.error('[RecipeHistoryScreen] Failed to toggle favorite:', error)
        setError(error.message || 'Failed to update favorite')
        setTimeout(() => setError(null), 3000)
      }
    } catch (error: any) {
      console.error('[RecipeHistoryScreen] Failed to toggle favorite:', error)
      setError(error.message || 'Failed to update favorite')
      setTimeout(() => setError(null), 3000)
    }
  }

  // Notes are now handled directly by RecipeCard component
  // RecipeCard manages notes via recipeNotesService.updateNotes()

  const filteredRecipes = recipes.filter(recipe => {
    if (filter === 'all') return true
    if (filter === 'favorites') return recipe.isFavorite === true
    if (filter === 'liked') return recipe.thumbsRating === 1
    if (filter === 'disliked') return recipe.thumbsRating === -1
    // meal type filters
    return recipe.mealType === filter
  })

  const getRatingStats = () => {
    const liked = recipes.filter(r => r.thumbsRating === 1).length
    const disliked = recipes.filter(r => r.thumbsRating === -1).length
    const favorited = recipes.filter(r => r.isFavorite === true).length
    const unrated = recipes.filter(r => r.thumbsRating === 0).length
    return { liked, disliked, favorited, unrated, total: recipes.length }
  }

  const stats = getRatingStats()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loading variant="spinner" size="md" className="mx-auto mb-2" />
          <p className="text-gray-600">Loading recipe history...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-warm-cream">
      {/* Sticky Header - Compact Design */}
      <header className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-2.5">
          <div className="flex items-center justify-between gap-3">
            {/* Back button + Title */}
            <button
              onClick={onBack || (() => window.history.back())}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1.5 text-gray-700"
            >
              <ICONS.navigation.back className={getIconSize('sm')} />
              <span className="text-sm font-medium hidden sm:inline">Back</span>
            </button>

            <div className="flex-1 min-w-0">
              <h1 className="text-base font-bold text-gray-900">Recipe History</h1>
            </div>

            {/* Inline stats - Total + Favorites only */}
            {stats.total > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-600">{stats.total} recipes</span>
                <span className="text-gray-300">•</span>
                <span className="text-toast-gold font-medium flex items-center gap-1">
                  <ICONS.food.heart className={getIconSize('xs')} />
                  {stats.favorited}
                </span>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>
      </header>

      {/* Filters - Single Tier */}
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex gap-2 flex-wrap items-center">
          {[
            { key: 'all' as SingleTierFilter, label: 'All', icon: null, emoji: null },
            { key: 'favorites' as SingleTierFilter, label: 'Favorites', icon: <ICONS.food.heart className={getIconSize('xs')} />, emoji: null },
            { key: 'liked' as SingleTierFilter, label: 'Liked', icon: <ICONS.actions.like className={getIconSize('xs')} />, emoji: null },
            { key: 'disliked' as SingleTierFilter, label: 'Disliked', icon: <ICONS.actions.dislike className={getIconSize('xs')} />, emoji: null },
            { key: 'breakfast' as SingleTierFilter, label: 'Breakfast', icon: null, emoji: MEAL_TYPE_EMOJIS.breakfast },
            { key: 'lunch' as SingleTierFilter, label: 'Lunch', icon: null, emoji: MEAL_TYPE_EMOJIS.lunch },
            { key: 'dinner' as SingleTierFilter, label: 'Dinner', icon: null, emoji: MEAL_TYPE_EMOJIS.dinner },
            { key: 'snacks' as SingleTierFilter, label: 'Snacks', icon: null, emoji: MEAL_TYPE_EMOJIS.snacks }
          ].map((option) => (
            <button
              key={option.key}
              onClick={() => setFilter(option.key)}
              aria-pressed={filter === option.key}
              className={`px-2.5 py-1.5 rounded-full text-xs font-medium transition-all border-2 flex items-center gap-1 ${
                filter === option.key
                  ? 'bg-toast-gold text-white border-toast-gold'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
              }`}
            >
              {option.icon}
              {option.emoji && <span className="text-sm">{option.emoji}</span>}
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Recipe Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-8">
        {filteredRecipes.length === 0 ? (
          recipes.length === 0 ? (
            <EmptyState
              icon={<ICONS.food.utensils className={`${getIconSize('2xl')} text-gray-400`} />}
              title="No Recipe History"
              description="Complete a meal plan to start building your recipe history."
            />
          ) : (
            <EmptyState
              icon={<ICONS.actions.search className={`${getIconSize('2xl')} text-gray-400`} />}
              title="No Matching Recipes"
              description="Try adjusting your filters to see more recipes."
            />
          )
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRecipes.map((recipe, index) => (
              <div key={recipe.id} className="relative">
                {index > 0 && (
                  <div className="sm:hidden absolute -top-3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                )}
              <RecipeCard
                variant="history"
                recipe={{
                  id: recipe.id,
                  name: recipe.name,
                  mealType: recipe.mealType,
                  protein: undefined,
                  cookingMethod: undefined,
                  cuisine: undefined
                }}
                thumbsRating={recipe.thumbsRating}
                isFavorite={recipe.isFavorite}
                dateUsed={recipe.dateUsed}
                description={recipe.description}
                prepTime={recipe.prepTime}
                cookTime={recipe.cookTime}
                servings={recipe.servings}
                recipeDetails={recipe.recipeData ? {
                  recipe: recipe.recipeData,
                  sessionId: null,
                  kitchenTools: recipe.recipeData.kitchenTools || [],
                  cookingTips: recipe.recipeData.cookingTips || []
                } : undefined}
                onThumbsUp={() => handleThumbsUp(recipe)}
                onThumbsDown={() => handleThumbsDown(recipe)}
                onToggleFavorite={() => handleToggleFavorite(recipe)}
                // Notes are handled internally by RecipeCard
              />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom spacing */}
      <div className="h-20"></div>
    </div>
  )
}

export default RecipeHistoryScreen